/** @format */

import React from "react";
import {
	Box,
	Container,
	Typography,
	Grid,
	Card,
	CardContent,
	Button,
	styled,
	Paper,
} from "@mui/material";
import LockIcon from "@mui/icons-material/Lock";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import SecurityIcon from "@mui/icons-material/Security";
import GppGoodIcon from "@mui/icons-material/GppGood";
import ReceiptIcon from "@mui/icons-material/Receipt";
import {
	ArrowForward,
	AccountBalance,
	VerifiedUser,
	Payment,
	TaskAlt,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { colors } from "../../utility/types/constant";
import { CTAButtonsWrapper, PrimaryCTAButton } from "../banner-one/styles";

const HeroSection = styled(Box)(({ theme }) => ({
	background: colors.primary,
	color: "#fff",
	padding: "80px 0",
	textAlign: "center",
	fontFamily: "'Montserrat', sans-serif",
}));

const FeatureCard = styled(Card)(({ theme }) => ({
	height: "100%",
	display: "flex",
	flexDirection: "column",
	transition: "transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out",
	"&:hover": {
		transform: "translateY(-8px)",
		boxShadow: "0 12px 24px rgba(0,0,0,0.15)",
	},
}));

const IconWrapper = styled(Box)(({ theme }) => ({
	width: "64px",
	height: "64px",
	borderRadius: "50%",
	display: "flex",
	alignItems: "center",
	justifyContent: "center",
	margin: "0 auto 16px",
	backgroundColor: colors.mainSecondary,
	color: "#fff",
	boxShadow: "0 8px 20px rgba(30, 193, 149, 0.3)",
}));

const StepCard = styled(Paper)(({ theme }) => ({
	backgroundColor: "#ffffff",
	borderRadius: "20px",
	padding: "40px 30px",
	boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
	border: "2px solid rgba(0,0,0,0.05)",
	transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
	textAlign: "center",
	position: "relative",
	overflow: "hidden",
	height: "100%",
	"&::before": {
		content: '""',
		position: "absolute",
		top: 0,
		left: 0,
		right: 0,
		height: "4px",
		background: colors.mainSecondary,
		transform: "scaleX(0)",
		transition: "transform 0.4s ease",
	},
	"&:hover": {
		transform: "translateY(-10px)",
		boxShadow: "0 20px 40px rgba(30, 193, 149, 0.2)",
		borderColor: colors.mainSecondary,
		"&::before": {
			transform: "scaleX(1)",
		},
	},
	[theme.breakpoints.down("md")]: {
		padding: "30px 20px",
	},
}));

const StepNumber = styled(Typography)(({ theme }) => ({
	position: "absolute",
	top: "10px",
	right: "15px",
	fontFamily: "'Montserrat', sans-serif",
	fontSize: "clamp(48px, 6vw, 72px)",
	fontWeight: 900,
	color: "rgba(30, 193, 149, 0.08)",
	lineHeight: 1,
}));

const StepIconWrapper = styled(Box)(({ theme }) => ({
	width: "80px",
	height: "80px",
	borderRadius: "50%",
	backgroundColor: colors.mainSecondary,
	display: "flex",
	alignItems: "center",
	justifyContent: "center",
	margin: "0 auto 24px auto",
	color: "#ffffff",
	transition: "all 0.3s ease",
	boxShadow: "0 8px 20px rgba(30, 193, 149, 0.3)",
}));

const StepTitle = styled(Typography)(({ theme }) => ({
	fontFamily: "'Montserrat', sans-serif",
	fontSize: "clamp(1.25rem, 2vw, 1.5rem)",
	fontWeight: 700,
	color: colors.primary,
	marginBottom: "12px",
}));

const StepDescription = styled(Typography)(({ theme }) => ({
	fontFamily: "'Montserrat', sans-serif",
	fontSize: "0.9375rem",
	fontWeight: 400,
	color: "#6c757d",
	lineHeight: 1.7,
}));

const ConnectorLine = styled(Box)(({ theme }) => ({
	display: "flex",
	alignItems: "center",
	justifyContent: "center",
	[theme.breakpoints.down("md")]: {
		display: "none",
	},
}));

const AnimatedArrow = styled(Box)(({ theme }) => ({
	color: colors.mainSecondary,
	animation: "slideRight 2s ease-in-out infinite",
	"@keyframes slideRight": {
		"0%, 100%": {
			transform: "translateX(0)",
		},
		"50%": {
			transform: "translateX(10px)",
		},
	},
}));

const CTAButton = styled(Button)(({ theme }) => ({
	backgroundColor: colors.mainSecondary,
	color: "#fff",
	padding: "12px 32px",
	fontSize: "16px",
	fontWeight: 600,
	borderRadius: "8px",
	textTransform: "none",
	"&:hover": {
		backgroundColor: colors.mainSecondary,
		transform: "scale(1.05)",
	},
}));

const SecurePayments: React.FC = () => {
	const navigate = useNavigate();
	const handleJoinClick = () => {
		navigate("/circle/sign-up");
	};

	const features = [
		{
			icon: <LockIcon sx={{ fontSize: 40 }} />,
			title: "Bank-Level Encryption",
			description:
				"All transactions are protected with 256-bit SSL encryption. Your payment data is never stored on our servers.",
		},
		{
			icon: <VerifiedUserIcon sx={{ fontSize: 40 }} />,
			title: "PCI Compliant",
			description:
				"We meet the highest Payment Card Industry standards. Your financial information is handled with maximum security.",
		},
		{
			icon: <CreditCardIcon sx={{ fontSize: 40 }} />,
			title: "Multiple Payment Methods",
			description:
				"Accept credit cards, debit cards, bank transfers, and digital wallets. Give members flexible payment options.",
		},
		{
			icon: <SecurityIcon sx={{ fontSize: 40 }} />,
			title: "Fraud Protection",
			description:
				"Advanced fraud detection monitors every transaction. Suspicious activity is flagged and investigated immediately.",
		},
		{
			icon: <GppGoodIcon sx={{ fontSize: 40 }} />,
			title: "Buyer Protection",
			description:
				"Disputes are handled fairly with a transparent resolution process. Your contributions are protected.",
		},
		{
			icon: <ReceiptIcon sx={{ fontSize: 40 }} />,
			title: "Instant Receipts",
			description:
				"Automatic email confirmations for every transaction. Complete payment history available in your account.",
		},
	];

	const howItWorksSteps = [
		{
			step: "01",
			icon: <AccountBalance sx={{ fontSize: 40 }} />,
			title: "Link Your Account",
			description:
				"Connect your bank account, credit card, or debit card securely. All information is encrypted and protected.",
		},
		{
			step: "02",
			icon: <VerifiedUser sx={{ fontSize: 40 }} />,
			title: "Verify Identity",
			description:
				"Complete a one-time verification for added security. This protects you and other members from fraud.",
		},
		{
			step: "03",
			icon: <Payment sx={{ fontSize: 40 }} />,
			title: "Make Payments",
			description:
				"Send or receive money with confidence. Transactions are processed instantly with full encryption.",
		},
		{
			step: "04",
			icon: <TaskAlt sx={{ fontSize: 40 }} />,
			title: "Track & Confirm",
			description:
				"Receive instant confirmation for every payment. View complete transaction history in your dashboard.",
		},
	];

	const benefits = [
		"Military-grade encryption protects all transactions",
		"Compliance with international payment security standards",
		"Zero storage of sensitive payment information",
		"Real-time fraud monitoring and prevention",
		"Secure dispute resolution process",
		"24/7 transaction monitoring and support",
	];

	return (
		<Box sx={{ bgcolor: "#f5f5f5" }}>
			{/* Hero Section */}
			<HeroSection>
				<Container maxWidth="lg">
					<Typography
						variant="h2"
						sx={{
							fontWeight: 700,
							mb: 2,
							fontFamily: "'Montserrat', sans-serif",
						}}>
						Secure Payments
					</Typography>
					<Typography
						variant="h5"
						sx={{
							mb: 4,
							opacity: 0.9,
							fontFamily: "'Montserrat', sans-serif",
						}}>
						Your Money, Safe & Protected
					</Typography>
					<Typography
						variant="h6"
						sx={{
							maxWidth: "800px",
							margin: "0 auto",
							mb: 4,
							fontFamily: "'Montserrat', sans-serif",
						}}>
						Experience peace of mind with every transaction. Our secure payment
						platform uses bank-level encryption and industry-leading security
						measures to protect your financial information.
					</Typography>
					<CTAButtonsWrapper
						data-aos="fade-up"
						data-aos-duration="1000"
						data-aos-delay="400">
						<PrimaryCTAButton
							variant="contained"
							size="large"
							onClick={handleJoinClick}>
							Get Started Securely
						</PrimaryCTAButton>
					</CTAButtonsWrapper>
				</Container>
			</HeroSection>

			{/* What is Secure Payments */}
			<Container maxWidth="lg" sx={{ py: 8 }}>
				<Typography
					variant="h3"
					align="center"
					sx={{
						fontWeight: 800,
						mb: 2,
						color: colors.primary,
						fontFamily: "'Montserrat', sans-serif",
						fontSize: "clamp(2rem, 4vw, 3rem)",
						letterSpacing: "-0.02em",
					}}>
					Why Security Matters
				</Typography>
				<Typography
					variant="body1"
					align="center"
					sx={{
						maxWidth: "900px",
						margin: "0 auto 6rem",
						fontSize: "clamp(1rem, 2vw, 1.25rem)",
						color: "#6c757d",
						fontFamily: "'Montserrat', sans-serif",
						lineHeight: 1.7,
					}}>
					In today's digital world, payment security isn't optional, it's
					essential. At FundsyFi, we take your financial security seriously.
					Every transaction is protected with the same level of encryption used
					by major banks. Our platform is PCI-DSS compliant and undergoes
					regular security audits to ensure your money and information are
					always safe.
				</Typography>

				{/* Features Grid */}
				<Grid container spacing={4} sx={{ mb: 8 }}>
					{features.map((feature, index) => (
						<Grid item xs={12} sm={6} md={4} key={index}>
							<FeatureCard>
								<CardContent>
									<IconWrapper>{feature.icon}</IconWrapper>
									<Typography
										variant="h6"
										sx={{ fontWeight: 600, mb: 2, color: "#333" }}>
										{feature.title}
									</Typography>
									<Typography
										variant="body2"
										sx={{ color: "#666", lineHeight: 1.7 }}>
										{feature.description}
									</Typography>
								</CardContent>
							</FeatureCard>
						</Grid>
					))}
				</Grid>
			</Container>

			{/* How It Works */}
			<Box sx={{ bgcolor: "#fff", py: 8 }}>
				<Container maxWidth="lg">
					<Typography
						variant="h3"
						align="center"
						sx={{
							fontWeight: 800,
							mb: 2,
							color: colors.primary,
							fontFamily: "'Montserrat', sans-serif",
							fontSize: "clamp(2rem, 4vw, 3rem)",
							letterSpacing: "-0.02em",
						}}>
						How It Works
					</Typography>
					<Typography
						variant="body1"
						align="center"
						sx={{
							maxWidth: "800px",
							margin: "0 auto 6rem",
							fontSize: "clamp(1rem, 2vw, 1.25rem)",
							color: "#6c757d",
							fontFamily: "'Montserrat', sans-serif",
							lineHeight: 1.7,
						}}>
						Set up secure payments in 4 simple steps and transact with complete
						confidence
					</Typography>

					<Box
						sx={{
							display: "grid",
							gridTemplateColumns: {
								xs: "1fr",
								md: "1fr auto 1fr auto 1fr auto 1fr",
							},
							gap: { xs: "40px", md: "20px" },
							alignItems: "center",
						}}>
						{howItWorksSteps.map((item, index) => (
							<React.Fragment key={index}>
								<StepCard elevation={2}>
									<StepNumber>{item.step}</StepNumber>
									<StepIconWrapper>{item.icon}</StepIconWrapper>
									<StepTitle>{item.title}</StepTitle>
									<StepDescription>{item.description}</StepDescription>
								</StepCard>

								{index < howItWorksSteps.length - 1 && (
									<ConnectorLine>
										<AnimatedArrow>
											<ArrowForward sx={{ fontSize: 32 }} />
										</AnimatedArrow>
									</ConnectorLine>
								)}
							</React.Fragment>
						))}
					</Box>
				</Container>
			</Box>

			{/* Benefits Section */}
			<Container maxWidth="lg" sx={{ py: 8 }}>
				<Typography
					variant="h3"
					align="center"
					sx={{
						fontWeight: 800,
						mb: 2,
						color: colors.primary,
						fontFamily: "'Montserrat', sans-serif",
						fontSize: "clamp(2rem, 4vw, 3rem)",
						letterSpacing: "-0.02em",
					}}>
					Your Security Guarantee
				</Typography>
				<Typography
					variant="body1"
					align="center"
					sx={{
						maxWidth: "800px",
						margin: "0 auto 6rem",
						fontSize: "clamp(1rem, 2vw, 1.25rem)",
						color: "#6c757d",
						fontFamily: "'Montserrat', sans-serif",
						lineHeight: 1.7,
					}}>
					Trust in a payment platform built with security at its core
				</Typography>

				<Grid
					container
					spacing={3}
					sx={{ maxWidth: "900px", margin: "0 auto" }}>
					{benefits.map((benefit, index) => (
						<Grid item xs={12} sm={6} key={index}>
							<Box sx={{ display: "flex", alignItems: "flex-start" }}>
								<Box
									sx={{
										width: "24px",
										height: "24px",
										borderRadius: "50%",
										backgroundColor: colors.mainSecondary,
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
										color: "#fff",
										fontSize: "14px",
										fontWeight: 600,
										mr: 2,
										flexShrink: 0,
										mt: 0.5,
									}}>
									✓
								</Box>
								<Typography
									variant="body1"
									sx={{
										color: "#6c757d",
										lineHeight: 1.7,
										fontFamily: "'Montserrat', sans-serif",
									}}>
									{benefit}
								</Typography>
							</Box>
						</Grid>
					))}
				</Grid>
			</Container>

			{/* CTA Section */}
			<Box
				sx={{
					backgroundColor: colors.primary,
					color: "#fff",
					py: 8,
				}}>
				<Container maxWidth="md" sx={{ textAlign: "center" }}>
					<Typography
						variant="h3"
						sx={{
							fontWeight: 700,
							mb: 2,
							fontFamily: "'Montserrat', sans-serif",
						}}>
						Ready to Experience Secure Payments?
					</Typography>
					<Typography
						variant="h6"
						sx={{
							mb: 4,
							opacity: 0.9,
							fontFamily: "'Montserrat', sans-serif",
						}}>
						Join FundsyFi today and enjoy peace of mind with every transaction
					</Typography>
					<Box
						sx={{
							display: "flex",
							gap: 2,
							justifyContent: "center",
							flexWrap: "wrap",
						}}>
						<CTAButton
							onClick={() => navigate("/circle/sign-up")}
							sx={{
								bgcolor: "#fff",
								color: colors.primary,
								"&:hover": {
									bgcolor: "#fff",
								},
							}}>
							Get Started Now
						</CTAButton>
						<CTAButton
							onClick={() => navigate("/circle/sign-in")}
							sx={{
								bgcolor: "transparent",
								border: "2px solid #fff",
								"&:hover": {
									bgcolor: "rgba(255,255,255,0.1)",
								},
							}}>
							Sign In
						</CTAButton>
					</Box>
				</Container>
			</Box>
		</Box>
	);
};

export default SecurePayments;
